import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../../api/adminApi";

interface Props {
  contactId: string;
  contactEmail: string;
  onDeliveryResumed: () => void;
  onClose: () => void;
}

const resumeContactEmailDelivery = withErrorHandling(adminApi.resumeContactEmailDelivery);

const ResumeDeliveryDialog = ({ contactId, contactEmail, onDeliveryResumed, onClose }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    const [, error] = await resumeContactEmailDelivery(contactId);
    setIsLoading(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[ResumeDeliveryDialog] resumeContactEmailDelivery");
      return;
    }

    sendNotification("Delivery resumed. Emails to this address will be attempted again.");
    onDeliveryResumed();
  };

  const handleDialogClose = () => {
    if (isLoading) {
      return;
    }
    onClose();
  };

  return (
    <SmallDialog open onClose={handleDialogClose}>
      <DialogTitle>Resume delivery?</DialogTitle>
      {!isLoading && <DialogCloseButton onClick={onClose} />}
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            This removes{" "}
            <Typography component="strong" variant="inherit">
              {contactEmail}
            </Typography>{" "}
            from our email provider&apos;s blocked list, so we start trying to deliver to it again.
          </Typography>
          <Typography>
            Only do this once the mailbox problem is fixed. If the address still rejects mail, it will be blocked again
            on the next bounce.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={isLoading}>
        <Button variant="contained" loading={isLoading} onClick={handleSubmit}>
          Resume delivery
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ResumeDeliveryDialog;
